import React from 'react';
import { usePage } from '@inertiajs/react';

import NewsCard, { type NewsItem } from '@/components/public/cards/NewsCard';
import PageHero from '@/components/public/sections/PageHero';
import Seo from '@/components/public/Seo';
import CategoryFilter from '@/components/public/ui/CategoryFilter';
import Section from '@/components/public/ui/Section';
import { NewsPageDetails } from '@/data/pages/news/details';
import { trackUmamiEvent } from '@/lib/analytics';

type Props = {
  news: NewsItem[];
  categories: string[];
};

export default function NewsIndex() {
  const { news, categories } = usePage<Props>().props;

  const [activeCategories, setActiveCategories] = React.useState<string[]>(['Todos']);

  const filteredNews = React.useMemo(() => {
    if (activeCategories.includes('Todos')) return news;
    return news.filter((n) => n.category && activeCategories.includes(n.category));
  }, [news, activeCategories]);

  const handleCategoryChange = (cats: string[]) => {
    setActiveCategories(cats);
    const resultCount = cats.includes('Todos')
      ? news.length
      : news.filter((n) => n.category && cats.includes(n.category)).length;

    trackUmamiEvent('news_filter', {
      categories: cats.join(','),
      result_count: resultCount,
    });
  };

  return (
    <>
      <Seo
        title="Notícias"
        description="Mantenha-se informado sobre tudo o que acontece na nossa freguesia."
      />
      <PageHero
        badge={NewsPageDetails.badge}
        title={NewsPageDetails.title}
        accentWord={NewsPageDetails.accentWord}
        subtitle={NewsPageDetails.subtitle}
      />
      <Section spacing="md">
        <CategoryFilter
          categories={categories}
          active={activeCategories}
          onChange={handleCategoryChange}
          className="mb-8"
        />
        {filteredNews.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredNews.map((item) => (
              <NewsCard key={item.slug} {...item} />
            ))}
          </div>
        ) : (
          <div className="surface-card p-12 text-center">
            <p className="text-on-surface-light text-lg">
              Nenhuma notícia encontrada nesta categoria.
            </p>
          </div>
        )}
      </Section>
    </>
  );
}
